import { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format, addMonths, endOfMonth, startOfMonth, parseISO } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AlertTriangle, Calendar, FileWarning, UserX } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const TERMINATION_REASONS = [
  { value: "voluntary_resignation", label: "Voluntary Resignation" },
  { value: "involuntary_termination", label: "Involuntary Termination" },
  { value: "layoff", label: "Layoff / Reduction in Force" },
  { value: "retirement", label: "Retirement" },
  { value: "end_of_contract", label: "End of Contract" },
  { value: "death", label: "Death" },
  { value: "disability", label: "Disability" },
  { value: "job_abandonment", label: "Job Abandonment" },
  { value: "misconduct", label: "Misconduct" },
  { value: "mutual_agreement", label: "Mutual Agreement" },
  { value: "other", label: "Other" },
];

const terminationSchema = z.object({
  terminationDate: z.string().min(1, "Termination date is required"),
  terminationReason: z.string().min(1, "Termination reason is required"),
  notes: z.string().optional(),
  acknowledged: z.boolean().refine((val) => val === true, {
    message: "You must acknowledge the impact of this termination",
  }),
});

type TerminationFormData = z.infer<typeof terminationSchema>;

interface ActiveEnrollment {
  id: number;
  planName: string;
  planType: string;
  coverageTier: string;
  terminationTiming?: string;
  employeePremium?: number;
  totalPremium?: number;
}

interface TerminateEmployeeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee: {
    id: number;
    firstName: string;
    lastName: string;
    hireDate?: string | null;
    email?: string;
  };
  activeEnrollments: ActiveEnrollment[];
  onConfirm: (data: {
    terminationDate: string;
    terminationReason: string;
    notes?: string;
  }) => void;
  isProcessing?: boolean;
}

function calculateCoverageEndDate(
  terminationDate: string,
  terminationTiming: string = "end_of_month"
): Date {
  const termDate = parseISO(terminationDate);
  
  switch (terminationTiming) {
    case "immediate":
      return termDate;
    case "mid_month":
      if (termDate.getDate() <= 15) {
        return new Date(termDate.getFullYear(), termDate.getMonth(), 15);
      } else {
        return endOfMonth(termDate);
      }
    case "end_of_month":
    default:
      return endOfMonth(termDate);
  }
}

function getTerminationTimingLabel(timing: string): string {
  switch (timing) {
    case "immediate":
      return "Immediate";
    case "mid_month":
      return "Mid-Month";
    case "end_of_month":
      return "End of Month";
    default:
      return timing;
  }
}

export function TerminateEmployeeModal({
  open,
  onOpenChange,
  employee,
  activeEnrollments,
  onConfirm,
  isProcessing = false,
}: TerminateEmployeeModalProps) {
  const form = useForm<TerminationFormData>({
    resolver: zodResolver(terminationSchema),
    defaultValues: {
      terminationDate: format(new Date(), "yyyy-MM-dd"),
      terminationReason: "",
      notes: "",
      acknowledged: false,
    },
  });

  const terminationDate = form.watch("terminationDate");

  const enrollmentImpacts = useMemo(() => {
    if (!terminationDate) return [];
    
    return activeEnrollments.map((enrollment) => {
      const coverageEndDate = calculateCoverageEndDate(
        terminationDate,
        enrollment.terminationTiming || "end_of_month"
      );
      
      return {
        ...enrollment,
        coverageEndDate,
        coverageEndDateFormatted: format(coverageEndDate, "MMMM d, yyyy"),
        terminationTimingLabel: getTerminationTimingLabel(
          enrollment.terminationTiming || "end_of_month"
        ),
      };
    });
  }, [terminationDate, activeEnrollments]);

  const hasCOBRAEligiblePlans = enrollmentImpacts.some(
    (e) => e.planType === "health" || e.planType === "dental" || e.planType === "vision"
  );

  const onSubmit = (data: TerminationFormData) => {
    onConfirm({
      terminationDate: data.terminationDate,
      terminationReason: data.terminationReason,
      notes: data.notes,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <UserX className="h-5 w-5" />
            Terminate Employee
          </DialogTitle>
          <DialogDescription>
            You are about to terminate {employee.firstName} {employee.lastName}. 
            This action will schedule the termination of all active benefit enrollments.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="terminationDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Termination Date *</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="date"
                          className="pl-10"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="terminationReason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Termination Reason *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select reason..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {TERMINATION_REASONS.map((reason) => (
                          <SelectItem key={reason.value} value={reason.value}>
                            {reason.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter any additional notes about this termination..."
                      className="resize-none"
                      rows={2}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Separator />

            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <FileWarning className="h-4 w-4 text-amber-500" />
                Impact Summary
              </h3>

              {enrollmentImpacts.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    The following active benefits will be terminated on their coverage end date:
                  </p>
                  
                  <div className="border rounded-lg divide-y">
                    {enrollmentImpacts.map((enrollment) => (
                      <div
                        key={enrollment.id}
                        className="p-3 flex items-center justify-between"
                      >
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{enrollment.planName}</span>
                            <Badge variant="outline" className="text-xs capitalize">
                              {enrollment.planType}
                            </Badge>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Coverage: {enrollment.coverageTier}
                          </div>
                        </div>
                        <div className="text-right space-y-1">
                          <div className="text-sm font-medium text-destructive">
                            Ends: {enrollment.coverageEndDateFormatted}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            ({enrollment.terminationTimingLabel})
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {hasCOBRAEligiblePlans && (
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>COBRA Notice Required</AlertTitle>
                      <AlertDescription>
                        This employee may be eligible for COBRA continuation coverage. 
                        A COBRA election notice should be sent within 14 days of the qualifying event.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground bg-muted/50 p-4 rounded-lg">
                  This employee has no active benefit enrollments to terminate.
                </div>
              )}

              {employee.hireDate && (
                <div className="text-sm text-muted-foreground">
                  <span className="font-medium">Hire Date:</span>{" "}
                  {format(parseISO(employee.hireDate), "MMMM d, yyyy")}
                </div>
              )}
            </div>

            <Separator />

            <FormField
              control={form.control}
              name="acknowledged"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-destructive/50 bg-destructive/5 p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-destructive font-medium">
                      I understand and acknowledge that:
                    </FormLabel>
                    <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1 mt-2">
                      <li>Benefits will remain active until their coverage end date, then be terminated</li>
                      <li>Coverage end dates are calculated based on each plan's termination settings</li>
                      <li>COBRA notices may need to be sent for qualifying health plans</li>
                      <li>This action cannot be easily undone</li>
                    </ul>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={isProcessing}
              >
                {isProcessing ? "Processing..." : "Process Termination"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
